// web/packages/shared-types/src/leagueTime.ts
//
// ⚠️ ONE HOME FOR "WHAT TIME IS IT IN THE LEAGUE'S TIMEZONE".
//
// Three callers ask this, and until 2026-09-11 all three answered it with their
// own `Intl.DateTimeFormat` call: the field-duty send (`apps/workers`, via
// `pacificParts`), the referee claim windows (`refereeWindows.ts`, via
// `tzParts`) and the twice-daily InLeague refresh cron (`apps/workers`, via
// `pacificHour`). Two of them had already drifted to different locales —
// `en-CA` in the first two, `en-US` in the third — each carrying its own copy
// of the same "Intl can emit 24 at midnight" comment because each had hit it
// separately.
//
// ⚠️ A FOURTH COPY SURVIVES, DELIBERATELY NAMED HERE RATHER THAN LEFT TO BE
// REDISCOVERED: `apps/stats/src/jobs/cardCrons.ts`'s own `tzParts` also returns
// a weekday, so it is not this function with a different name and folding it in
// would mean widening the return shape for one caller. It is filed rather than
// forced. Do not read "one home" above as "the only copy" until that is done.
//
// That is the display-rule-copied-into-each-surface shape this repo has paid
// for repeatedly, and the stakes are not cosmetic: one of these answers decides
// whether a volunteer gets mailed tonight, one decides whether a referee may
// claim a slot, and one decides whether a scheduled refresh runs at all. The
// three per-Worker functions are kept as NAMED PASSTHROUGHS over this module,
// so each call site keeps reading in its own vocabulary and none of them may
// ever grow a rule of its own.
//
// `Intl` rather than a fixed offset, because the offset is what changes.

export const LEAGUE_TZ = 'America/Los_Angeles'

/** League-timezone calendar date (YYYY-MM-DD), 24h hour and minute for an
 *  epoch-ms instant. DST-correct by construction. */
export function leagueParts(nowMs: number, tz: string = LEAGUE_TZ): { date: string; hour: number; minute: number } {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  })
  const p: Record<string, string> = {}
  for (const part of fmt.formatToParts(new Date(nowMs))) p[part.type] = part.value
  // Intl can render midnight as "24" in some runtimes; normalize to 0.
  return { date: `${p.year}-${p.month}-${p.day}`, hour: Number(p.hour) % 24, minute: Number(p.minute) }
}

/** The league-timezone calendar date (YYYY-MM-DD) for an epoch-ms instant.
 *
 *  ⚠️ THE SPELLING OF "TODAY" THAT EVERY BOARD COMPARES A GAME DATE AGAINST.
 *  It is league midnight, never UTC midnight: 17:00 Pacific is already tomorrow
 *  in UTC, so a UTC floor takes THAT EVENING'S games off a board at 5pm on a
 *  game day. `openBoardFloor` (apps/referee) and the mentor window both read
 *  this rather than each calling `leagueParts` and picking `.date` off it. */
export function leagueToday(nowMs: number, tz: string = LEAGUE_TZ): string {
  return leagueParts(nowMs, tz).date
}

/** The league-timezone hour (0-23) for an epoch-ms instant. */
export function leagueHour(nowMs: number, tz: string = LEAGUE_TZ): number {
  return leagueParts(nowMs, tz).hour
}

/** The epoch-ms instant of a wall-clock league date and `HH:MM`. Derived by
 *  probing the zone's offset at that instant, so it is DST-correct without any
 *  offset table. Moved from apps/stats/src/jobs/cardCrons.ts, which reads it
 *  for "has this game ended". */
export function leagueInstant(dateISO: string, timeHHMM: string, tz: string = LEAGUE_TZ): number {
  const naive = Date.parse(`${dateISO}T${timeHHMM}:00Z`)
  const probe = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, timeZoneName: 'longOffset',
  }).formatToParts(new Date(naive)).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+00:00'
  const m = /GMT([+-])(\d{2}):(\d{2})/.exec(probe)
  if (!m) return naive
  const sign = m[1] === '-' ? -1 : 1
  const offsetMs = sign * (Number(m[2]) * 60 + Number(m[3])) * 60_000
  return naive - offsetMs
}

/** Has a game at this league date and `HH:MM` kicked off as of `nowMs`? A game
 *  with no date never has.
 *
 *  ⚠️ NEVER THROWS, BECAUSE IT RUNS IN A BROWSER DURING RENDER. `leagueInstant`
 *  asks `Intl` for `timeZoneName: 'longOffset'`, which Safari before 15.4 and
 *  older Android WebViews reject with a RangeError. Uncaught, that blanks the
 *  referee's whole My assignments page; caught, the post-game links simply do
 *  not show on that phone. */
export function leagueKickoffPassed(nowMs: number, dateISO: string | null, timeHHMM: string): boolean {
  if (dateISO === null) return false
  try {
    return nowMs >= leagueInstant(dateISO, timeHHMM)
  } catch {
    return false
  }
}

/** A stored UTC instant (D1's `YYYY-MM-DD HH:MM:SS` or ISO) as the league's
 *  wall clock, "2026-09-27 10:42". An unreadable value comes back unchanged.
 *  The Reports screen and the Internal Incident Report both print with it. */
export function leagueStamp(at: string, tz: string = LEAGUE_TZ): string {
  const ms = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(at) ? at : `${at.replace(' ', 'T')}Z`)
  if (Number.isNaN(ms)) return at
  const p = leagueParts(ms, tz)
  return `${p.date} ${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`
}
